import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Phone, MessageCircle } from "lucide-react";

const ease = [0.25, 1, 0.5, 1];

const PROCESS = [
  {
    step: "01",
    title: "Site Walk-Through",
    body: "We visit the property, measure the area, evaluate sunlight, soil, and drainage, and recommend the right turfgrass for your yard.",
  },
  {
    step: "02",
    title: "Prep & Grading",
    body: "Old grass and weeds are removed, the soil is tilled and graded for proper drainage, and amendments are added if needed.",
  },
  {
    step: "03",
    title: "Sod Installation",
    body: "Fresh-cut sod rolls are laid tight, seam-to-seam, then rolled to ensure full root-to-soil contact for a fast establishment.",
  },
  {
    step: "04",
    title: "Aftercare Plan",
    body: "We give you a watering and mowing schedule for the first 30 days so your new lawn roots in healthy and green.",
  },
];

const TURF_TYPES = [
  {
    name: "Bermuda",
    blurb: "Tough, sun-loving, fast-growing — the classic Alabama choice for full-sun yards.",
  },
  {
    name: "Zoysia",
    blurb: "Dense, lush, and soft underfoot. Tolerates partial shade and recovers beautifully.",
  },
  {
    name: "Centipede",
    blurb: "Low-maintenance, slow-growing, and easy on the wallet — great for our climate.",
  },
  {
    name: "St. Augustine",
    blurb: "Broad-bladed and shade-tolerant — great for yards with mixed sun and tree cover.",
  },
];

const FAQ = [
  {
    q: "How long does sod installation take?",
    a: "Most residential jobs are completed in 1–2 days, depending on yard size and prep work needed.",
  },
  {
    q: "When can I walk on my new lawn?",
    a: "Keep foot traffic to a minimum for the first 2 weeks. After 3 weeks, your sod should be well-rooted and ready for normal use.",
  },
  {
    q: "What kind of watering does new sod need?",
    a: "Water deeply twice a day for the first 7–10 days, then taper down. We'll send you home with a specific schedule.",
  },
  {
    q: "What time of year is best to lay sod?",
    a: "In Alabama, warm-season grasses (Bermuda, Zoysia, Centipede) are best installed spring through early fall. Fescue can be laid in fall and early spring.",
  },
];

export default function SodInstallation() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F8F2] text-[#122412]">
      {/* Header */}
      <header className="absolute top-0 inset-x-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="back-home" className="flex items-center gap-3">
            <img
              src="/brand/yair-logo.jpg"
              alt="Yair Landscaping"
              className="h-12 w-auto rounded-md object-contain"
            />
            <span className="font-serif text-xl md:text-2xl tracking-tight text-[#F4F8F2]">
              Yair <span className="italic font-light">Landscaping</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="back-link"
            className="inline-flex items-center gap-2 overline text-[#F4F8F2] hover:text-[#7BBADB] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-[70vh] w-full overflow-hidden bg-[#1B3A1B]">
        <img
          src="/services/sod.jpg"
          alt="Fresh sod laid by Yair Landscaping"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1B3A1B] via-[#1B3A1B]/55 to-[#1B3A1B]/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 min-h-[70vh] flex flex-col justify-end pb-16 lg:pb-24 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease }}
            className="overline text-[#7BBADB] mb-6"
          >
            Service · Birmingham, AL
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 0.15 }}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#F4F8F2] font-light leading-[0.95] tracking-tight max-w-4xl"
          >
            Sod Installation
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.35 }}
            className="font-body mt-8 text-[#DDECD7] max-w-2xl text-lg leading-relaxed"
          >
            An instant, healthy, showroom-quality lawn — laid roll-by-roll by a crew that
            obsesses over seams, slope, and soil prep.
          </motion.p>
        </div>
      </section>

      {/* INTRO COPY */}
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="overline text-[#1F70BF] mb-6">What your new lawn will look like</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#122412] font-light leading-[1.05] tracking-tight">
                An instant upgrade,
                <br />
                <span className="italic">inside and out.</span>
              </h2>
            </div>
            <div className="md:col-span-7 font-body text-[#4A6B4A] text-lg leading-relaxed space-y-6">
              <p>
                A professionally installed sod lawn gives your property an instant upgrade in
                both appearance and value.
              </p>
              <ul className="space-y-3">
                {[
                  "Clean, uniform green lawn right away",
                  "No patchy or uneven growth",
                  "Strong, dense grass coverage",
                  "Sharp curb appeal from day one",
                  "Makes your home look well-maintained instantly",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#2E7D32] mt-1 shrink-0" strokeWidth={1.5} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                We install it the right way so it looks good immediately and continues to
                establish properly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TURF TYPES */}
      <section className="py-20 lg:py-28 bg-[#DDECD7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-14">
            <div className="overline text-[#1F70BF] mb-6">Grasses we install</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#122412] font-light leading-[1.05] tracking-tight max-w-3xl">
              The right turf,
              <br />
              <span className="italic">for your yard.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TURF_TYPES.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease, delay: i * 0.08 }}
                className="bg-[#F4F8F2] border border-[#C5D8B8] p-7 hover:border-[#2E7D32] transition-colors"
                data-testid={`turf-type-${i}`}
              >
                <h3 className="font-serif text-2xl text-[#122412] font-light mb-3">
                  {t.name}
                </h3>
                <p className="font-body text-sm text-[#4A6B4A] leading-relaxed">
                  {t.blurb}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-14">
            <div className="overline text-[#1F70BF] mb-6">Before & after</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#122412] font-light leading-[1.05] tracking-tight max-w-3xl">
              See the
              <br />
              <span className="italic">transformation.</span>
            </h2>
          </div>
          <div className="relative w-full overflow-hidden border border-[#C5D8B8] bg-[#1B3A1B]">
            <img
              src="/sod/before-after.jpg"
              alt="Sod installation — before and after"
              className="w-full h-auto block"
              data-testid="sod-before-after"
            />
          </div>
        </div>
      </section>

      {/* INCLUDES */}
      <section className="py-20 lg:py-28 bg-[#1B3A1B] text-[#F4F8F2] relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="overline text-[#7BBADB] mb-6">What's included</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#F4F8F2] font-light leading-[1.05] tracking-tight max-w-3xl mb-12">
            Everything from
            <br />
            <span className="italic text-[#DDECD7]">prep to final roll.</span>
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              "Free on-site measurement & consultation",
              "Removal of existing grass and weeds",
              "Soil tilling, grading, and leveling",
              "Premium locally-grown sod (your choice of turfgrass)",
              "Tight roll-to-roll installation",
              "Rolling for full root-to-soil contact",
              "Site cleanup and haul-away",
              "30-day watering & care plan",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 font-body">
                <CheckCircle2 className="w-5 h-5 text-[#7BBADB] mt-0.5 shrink-0" strokeWidth={1.5} />
                <span className="text-[#DDECD7] leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="mb-14">
            <div className="overline text-[#1F70BF] mb-6">Common questions</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#122412] font-light leading-[1.05] tracking-tight">
              Good to know.
            </h2>
          </div>
          <div className="space-y-6">
            {FAQ.map((f, i) => (
              <div
                key={f.q}
                className="border-t border-[#C5D8B8] pt-6"
                data-testid={`faq-${i}`}
              >
                <h3 className="font-serif text-xl md:text-2xl text-[#122412] font-light mb-3">
                  {f.q}
                </h3>
                <p className="font-body text-[#4A6B4A] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#DDECD7]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="overline text-[#1F70BF] mb-6">Ready to start?</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#122412] font-light leading-[1.05] tracking-tight mb-6">
            Let's make your yard
            <br />
            <span className="italic">look brand new.</span>
          </h2>
          <p className="font-body text-[#4A6B4A] text-lg leading-relaxed max-w-xl mx-auto mb-10">
            Free, no-obligation consultation. Most quotes returned within 1–2 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/#contact"
              data-testid="cta-quote"
              className="inline-flex items-center justify-center gap-3 bg-[#2E7D32] text-[#F4F8F2] px-10 py-5 overline hover:bg-[#388E3C] transition-colors"
            >
              Request a Free Quote
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <a
              href="tel:+12052033712"
              data-testid="cta-call"
              className="inline-flex items-center justify-center gap-3 border border-[#2E7D32] text-[#2E7D32] px-10 py-5 overline hover:bg-[#2E7D32] hover:text-[#F4F8F2] transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={1.5} /> (205) 203-3712
            </a>
            <a
              href="sms:+12052033712"
              data-testid="cta-text"
              className="inline-flex items-center justify-center gap-3 border border-[#1F70BF] text-[#1F70BF] px-10 py-5 overline hover:bg-[#1F70BF] hover:text-white transition-colors"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Text Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1B3A1B] text-[#DDECD7] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="overline hover:text-[#7BBADB]">
            ← Back to Yair Landscaping
          </Link>
          <span className="overline">© {new Date().getFullYear()} Yair Landscaping</span>
        </div>
      </footer>
    </div>
  );
}
